import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import { selectIsEditMode } from 'features/common/editModeSlice';
import dataValidationService from 'features/ImportData/dataValidationService';
import { CheckedImportDataResponse } from 'features/Store/models';
import { useState } from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError } from 'utils';
import ErrorDataDetailModal from '../ErrorDataDetailModal/ErrorDataDetailModal';
import useGlobalModal from '../GlobalModal/useGlobalModal';
import './ErrorDataSummaryPatternModal.scss';

export interface ErrorDataSummaryPatternModalProps {
  errorFromFeatureId: number; // 3, 14, 16
  data: CheckedImportDataResponse;
  onClose?: () => void;
}

function ErrorDataSummaryPatternModal({ errorFromFeatureId, data, onClose }: ErrorDataSummaryPatternModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal, closeGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // state
  const [errorSummary, setErrorSummary] = useState<CheckedImportDataResponse>(data);
  const [showErrorDataDetailModal, setShowErrorDataDetailModal] = useState<boolean>(false);

  function handleClose() {
    onClose?.();
    closeGlobalModal();
  }

  function handleOpenErrorDataDetailModal(item: CheckedImportDataResponse) {
    setShowErrorDataDetailModal(true);
  }

  function handleCloseErrorDataDetailModal(hasFixErrorData: boolean) {
    setShowErrorDataDetailModal(false);
    if (hasFixErrorData) {
      handleRefetchSummaryData();
    }
  }

  function handleRefetchSummaryData() {
    (async () => {
      try {
        const response = await dataValidationService.getErrorImportDataSummaryByPattern(data.counter_pattern_id);
        setErrorSummary(response.data);
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function renderErrorSummary() {
    let isShowButtonErrorDetail = false;
    if (data.total_unregister > 0) {
      isShowButtonErrorDetail = true;
    }
    // if (data.total_invalid_pos_code > 0) {
    //   isShowButtonErrorDetail = true;
    // }
    // if (data.total_menu_has_poscode_in_main_and_sub_menu > 0 || data.total_multiple_registered > 0) {
    //   isShowButtonErrorDetail = true;
    // }
    if (data.total_multiple_registered > 0) {
      isShowButtonErrorDetail = true;
    }
    if (data.total_poscode_used_in_multiple_menu_subtitle_menu > 0) {
      isShowButtonErrorDetail = true;
    }

    return (
      errorSummary && (
        <>
          <Row className="mb-3 d-flex align-items-center">
            <Col sm={10}>
              <strong>{t('ErrorDataDetailModal.overview_error_info1')}</strong>
            </Col>
            <Col sm={2} className="text-end">
              {isShowButtonErrorDetail && (
                <Button onClick={handleOpenErrorDataDetailModal.bind(null, errorSummary)}>
                  {t('ErrorDataSummaryModal.error_detail')}
                </Button>
              )}
            </Col>
          </Row>
          <ul className="mb-0">
            {/* pattern 1a */}
            {errorSummary.total_unregister > 0 && (
              <li className="mb-2">{`${t('ErrorDataSummaryModal.warn_error_pattern1')}`}</li>
            )}

            {/* pattern 2: remove from version 3.0 */}
            {/* {errorSummary.total_invalid_pos_code > 0 && (
              <li
                className="mb-2"
                dangerouslySetInnerHTML={{ __html: t('ErrorDataSummaryModal.warn_error_pattern2') }}
              ></li>
            )} */}

            {/* pattern 3: remove from version 3.0 */}
            {/* {(errorSummary.total_menu_has_poscode_in_main_and_sub_menu > 0 ||
              errorSummary.total_multiple_registered > 0) && (
              <li className="mb-2">{`${t('ErrorDataSummaryModal.warn_error_pattern3or4')}`}</li>
            )} */}
            {/* pattern 4 */}
            {errorSummary.total_multiple_registered > 0 && (
              <li className="mb-2">{`${t('ErrorDataSummaryModal.warn_error_pattern3or4')}`}</li>
            )}

            {/* pattern 5 */}
            {errorSummary.total_poscode_used_in_multiple_menu_subtitle_menu > 0 && (
              <li className="mb-2">{`${t('ErrorDataSummaryModal.warn_error_pattern5')}`}</li>
            )}
          </ul>
        </>
      )
    );
  }

  const errorSummaryContent = renderErrorSummary();

  return (
    <>
      <Modal
        size="lg"
        dialogClassName="error-data-summary-modal"
        className="alert-modal"
        show
        backdrop="static"
        centered
        onHide={handleClose}
      >
        <Modal.Header className="bg-primary justify-content-center">
          <Modal.Title className="fs-6 text-white">{t('App.error')}</Modal.Title>
        </Modal.Header>

        <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>{errorSummaryContent}</Modal.Body>

        <Modal.Footer className={`justify-content-center ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
          <Button variant="light" onClick={handleClose}>
            {t('Alert_Modal.close')}
          </Button>
        </Modal.Footer>
      </Modal>

      {showErrorDataDetailModal && (
        <ErrorDataDetailModal
          errorFromFeatureId={errorFromFeatureId}
          counterPatternId={data.counter_pattern_id}
          onClose={handleCloseErrorDataDetailModal}
        />
      )}
    </>
  );
}

export default ErrorDataSummaryPatternModal;
